import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  Event as EventIcon,
  Star as StarIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import MKButton from '../MKButton';
import { PRIMARY_PINK } from '../../constants';

const FeaturedEventCard = ({ event }) => {
  const navigate = useNavigate();

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMoreDetails = () => {
    navigate(`/events/${event.id}`);
  };

  const handleViewClub = () => {
    if (event.club_handle) {
      navigate(`/clubs/${event.club_handle}`);
    }
  };

  return (
    <Card 
      sx={{ 
        maxWidth: 400, 
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      {event.flyer_url && (
        <CardMedia
          component="img"
          height="400"
          image={event.flyer_url}
          alt={event.title}
          sx={{ 
            objectFit: 'contain',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0'
          }}
        />
      )}
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 600,
              color: PRIMARY_PINK,
              flexGrow: 1,
              mr: 1
            }}
          >
            {event.title}
          </Typography>
          {event.priority && event.priority > 5 && (
            <Chip
              icon={<StarIcon />}
              label="Featured"
              size="small"
              color="warning"
              sx={{ flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Club Info */}
        {event.club_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {event.club_avatar_url && (
              <Avatar 
                src={event.club_avatar_url} 
                alt={event.club_name}
                sx={{ 
                  width: 24, 
                  height: 24, 
                  mr: 1,
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {event.club_name}
            </Typography>
          </Box>
        )}

        {/* Event Date/Time */}
        {event.start_time && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <EventIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(event.start_time)}
            </Typography>
          </Box>
        )}

        {/* Location */}
        {(event.location || event.location_url) && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <LocationIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary', mt: 0.2 }} />
            {event.location_url ? (
              <Typography
                variant="body2"
                component="a"
                href={event.location_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: PRIMARY_PINK,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {event.location_display_name || event.location || 'TBD'}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {event.location}
              </Typography>
            )}
          </Box>
        )}

        {/* Description */}
        <Box
          sx={{ 
            mb: 2,
            flexGrow: 1,
            maxHeight: '7.0em', // Approximately 6 lines (1.4 * 6)
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              whiteSpace: 'pre-line',
              lineHeight: 1.4
            }}
          >
            {event.description}
          </Typography>
          {/* Ellipsis at the end of visible content */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              background: 'inherit',
              paddingLeft: '0.5em',
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}
          >
            ...
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <MKButton
            variant="contained"
            color="primary"
            onClick={handleMoreDetails}
            fullWidth
            sx={{
              backgroundColor: PRIMARY_PINK,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: PRIMARY_PINK,
                opacity: 0.9
              }
            }}
          >
            More Details
          </MKButton>
          {event.club_handle && (
            <MKButton
              variant="outlined"
              onClick={handleViewClub}
              fullWidth
              sx={{
                borderColor: PRIMARY_PINK,
                color: PRIMARY_PINK,
                '&:hover': {
                  borderColor: PRIMARY_PINK,
                  backgroundColor: 'rgba(233, 30, 99, 0.08)'
                }
              }}
            >
              View Club
            </MKButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeaturedEventCard; 